import {motion} from 'framer-motion';
import {Tooltip} from 'platform/components';
import {
  Box,
  Clickable,
  getCssSize,
  Grid,
  Icon,
  Integer,
  Stack,
  HStack,
  Text,
  useDevice,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DashboardTileSize} from '../types/DashboardTileSize';
import {OpportunityType} from '../types/OpportunityType';
import {ColumnTransformation} from '../utils/createOpportunitiesFunnelColumns';
import {AnimatedFunnelValue} from './AnimatedFunnelValue';

const SALES_FUNNEL_GRAPH_BAR_HEIGHT = 34;
const SALES_FUNNEL_GRAPH_BAR_HEIGHT_MOBILE = 6;

// The phone bar grows sideways at a fixed height; elsewhere it grows downwards from a minimum.
const GRAPH_BAR_HEIGHT: ValueByDevice<Integer | undefined> = [
  SALES_FUNNEL_GRAPH_BAR_HEIGHT_MOBILE,
  undefined,
  undefined,
  undefined,
];
const GRAPH_BAR_MIN_HEIGHT: ValueByDevice<Integer | undefined> = [
  undefined,
  SALES_FUNNEL_GRAPH_BAR_HEIGHT,
  SALES_FUNNEL_GRAPH_BAR_HEIGHT,
  SALES_FUNNEL_GRAPH_BAR_HEIGHT,
];

const METRIC_COLUMNS_BY_SIZE: Record<DashboardTileSize, ValueByDevice<number>> = {
  quarter: [1, 6, 6, 6],
  half: [1, 6, 6, 6],
  tall: [1, 3, 3, 3],
  wide: [1, 6, 6, 6],
  large: [1, 6, 6, 6],
};

interface OpportunitiesFunnelMetricsProps extends TestIdProps {
  columns: ColumnTransformation[];
  size: DashboardTileSize;
  isRedirecting: boolean;
  onSelect: (id: OpportunityType) => void;
}

export function OpportunitiesFunnelMetrics(props: OpportunitiesFunnelMetricsProps) {
  const device = useDevice();
  const isMobile = device === 'mobile';

  return (
    <Grid
      columns={METRIC_COLUMNS_BY_SIZE[props.size]}
      spacing={[1, 2, 2, 2]}
      grow={1}
      autoRows="minmax(0, 1fr)"
      data-testid={suffixTestId('metrics', props)}
    >
      {props.columns.map((column) => (
        <Box height="100%" key={column.id}>
          <Clickable
            height="100%"
            onClick={() => props.onSelect(column.id)}
            isDisabled={props.isRedirecting}
          >
            <HoverBox $isFullHeight>
              <VStack basis="100%" height="100%" align="stretch" spacing={[1, 4, 4, 4]}>
                <Stack
                  direction={['row', 'column', 'column', 'column']}
                  align={['center', 'stretch', 'stretch', 'stretch']}
                  justify="space-between"
                  spacing={[1, 4, 4, 4]}
                  grow={isMobile ? undefined : 1}
                >
                  <HStack align="center" spacing={1}>
                    <Text color="secondary" size="xSmall">
                      {column.title}
                    </Text>
                    <Tooltip description={column.tooltip}>
                      <Icon value="action/help" size={4} tabIndex={-1} />
                    </Tooltip>
                  </HStack>
                  <AnimatedFunnelValue value={column.value} />
                </Stack>
                <HStack
                  height={GRAPH_BAR_HEIGHT}
                  minHeight={GRAPH_BAR_MIN_HEIGHT}
                  grow={isMobile ? undefined : 1}
                  align={['stretch', 'flex-end', 'flex-end', 'flex-end']}
                >
                  <motion.div
                    style={isMobile ? {height: '100%'} : {width: '100%'}}
                    animate={isMobile ? {width: column.height} : {height: column.height}}
                    transition={{type: 'tween'}}
                  >
                    <Box height="100%" backgroundColor={column.color} />
                  </motion.div>
                </HStack>
              </VStack>
            </HoverBox>
          </Clickable>
        </Box>
      ))}
    </Grid>
  );
}

const HoverBox = styled.div<{$isFullHeight?: boolean}>`
  height: ${({$isFullHeight}) => ($isFullHeight ? '100%' : 'auto')};
  padding: ${getCssSize(2)};
  border-radius: ${({theme}) => theme.radii.medium};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][40]};
  }
`;
